type BodyScrollState = {
  overflow: string;
  position: string;
  top: string;
  width: string;
  scrollY: number;
};

const activeLocks = new Map<string, number>();
let bodyScrollState: BodyScrollState | null = null;

const isDomAvailable = () => typeof window !== "undefined" && typeof document !== "undefined";

function applyBodyLock(): void {
  if (!isDomAvailable() || bodyScrollState !== null) {
    return;
  }

  const currentScrollY =
    window.scrollY ?? window.pageYOffset ?? document.documentElement?.scrollTop ?? document.body?.scrollTop ?? 0;

  bodyScrollState = {
    overflow: document.body.style.overflow || "",
    position: document.body.style.position || "",
    top: document.body.style.top || "",
    width: document.body.style.width || "",
    scrollY: currentScrollY,
  };

  document.body.style.overflow = "hidden";
  document.body.style.position = "fixed";
  document.body.style.top = `-${currentScrollY}px`;
  document.body.style.width = "100%";
}

function restoreBodyLock(): void {
  if (!isDomAvailable() || !bodyScrollState) {
    return;
  }

  const { overflow, position, top, width, scrollY } = bodyScrollState;
  bodyScrollState = null;

  if (overflow) {
    document.body.style.overflow = overflow;
  } else {
    document.body.style.removeProperty("overflow");
  }

  if (position) {
    document.body.style.position = position;
  } else {
    document.body.style.removeProperty("position");
  }

  if (top) {
    document.body.style.top = top;
  } else {
    document.body.style.removeProperty("top");
  }

  if (width) {
    document.body.style.width = width;
  } else {
    document.body.style.removeProperty("width");
  }

  try {
    window.scrollTo(0, scrollY);
  } catch {
    // ignore scroll restoration issues (e.g., jsdom)
  }
}

export function acquireBodyScrollLock(source: string): void {
  if (!isDomAvailable()) return;

  const count = activeLocks.get(source) ?? 0;
  activeLocks.set(source, count + 1);

  if (bodyScrollState === null) {
    applyBodyLock();
    return;
  }

  // If another source already applied the lock, ensure styles stay enforced.
  document.body.style.overflow = "hidden";
  document.body.style.position = "fixed";
  document.body.style.width = "100%";
}

export function releaseBodyScrollLock(source: string): void {
  if (!isDomAvailable()) return;

  const count = activeLocks.get(source);
  if (count === undefined) {
    return;
  }

  if (count > 1) {
    activeLocks.set(source, count - 1);
    return;
  }

  activeLocks.delete(source);

  if (activeLocks.size === 0) {
    restoreBodyLock();
  }
}

export function __dangerousResetBodyScrollLockForTests(): void {
  if (!isDomAvailable()) {
    activeLocks.clear();
    bodyScrollState = null;
    return;
  }

  activeLocks.clear();

  if (bodyScrollState) {
    restoreBodyLock();
  } else {
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("position");
    document.body.style.removeProperty("top");
    document.body.style.removeProperty("width");
  }
}

export function syncBodyScrollLockOffset(scrollY: number): void {
  if (!isDomAvailable() || !bodyScrollState) {
    return;
  }

  bodyScrollState.scrollY = scrollY;
  document.body.style.top = `-${scrollY}px`;
}
